import { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { useIamCatalog } from '../../hooks/useIam';
import { buildPermissionLookup } from '../../components/access/iamLookups';
import { AccessStateNotice } from '../../components/access/AccessStateNotice';
import {
    groupRoutesByDomain,
    migratedProgressPct
} from '../../components/access/routes/routeDomainGrouping';
import { RouteDomainSection } from '../../components/access/routes/RouteDomainSection';
import { RouteStatusCards } from '../../components/access/routes/RouteStatusCards';
import FilterDropdown from '../../components/filtering/FilterDropdown';
import type { RouteInfo, RouteStatus } from '../../services/iam';

const SEARCH_DEBOUNCE_MS = 300;

// Matches path, permission, OR legacy guard (case-insensitive substring) -
// covers both "what guards this path" and "what's left to migrate" queries.
function routeMatchesQuery(route: RouteInfo, query: string): boolean {
    if (route.path.toLowerCase().includes(query)) return true;
    if ((route.permission ?? '').toLowerCase().includes(query)) return true;
    return route.legacyGuards.some((guard) => guard.toLowerCase().includes(query));
}

export function AccessRoutes() {
    usePageTitle('Access · Routes');
    const { setTitle, setSubtitle } = usePageHeader();
    const { catalog, isLoading, isError, refresh } = useIamCatalog();

    const [statusFilter, setStatusFilter] = useState<RouteStatus[]>([]);
    // "Domain" is display-only vocabulary; the catalog field is `route.area`.
    const [domainFilter, setDomainFilter] = useState<string[]>([]);
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    // Closed by default (~350 routes across a dozen domains); manual state so
    // an expanded domain survives a filter being applied and cleared.
    const [openDomains, setOpenDomains] = useState<ReadonlySet<string>>(new Set());

    useEffect(() => {
        const handle = setTimeout(
            () => setDebouncedQuery(query.trim().toLowerCase()),
            SEARCH_DEBOUNCE_MS
        );
        return () => clearTimeout(handle);
    }, [query]);

    const toggleDomain = (domain: string) => {
        setOpenDomains((prev) => {
            const next = new Set(prev);
            if (next.has(domain)) {
                next.delete(domain);
            } else {
                next.add(domain);
            }
            return next;
        });
    };

    const permissionLookup = useMemo(
        () => buildPermissionLookup(catalog?.permissions ?? []),
        [catalog]
    );

    const domainOptions = useMemo(() => {
        const counts = new Map<string, number>();
        for (const route of catalog?.routes ?? []) {
            counts.set(route.area, (counts.get(route.area) ?? 0) + 1);
        }
        return Array.from(counts.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([domain, count]) => ({
                value: domain,
                label: domain,
                sublabel: `${count} route${count === 1 ? '' : 's'}`
            }));
    }, [catalog]);

    // Status counts respect the domain/search filters but not the status
    // filter itself, so the cards don't freeze once one is selected.
    const routesForStatusCounts = useMemo(() => {
        const routes = catalog?.routes ?? [];
        return routes.filter((route) => {
            if (domainFilter.length > 0 && !domainFilter.includes(route.area)) return false;
            if (debouncedQuery && !routeMatchesQuery(route, debouncedQuery)) return false;
            return true;
        });
    }, [catalog, domainFilter, debouncedQuery]);

    const statusCounts = useMemo(() => {
        const counts: Record<RouteStatus, number> = { migrated: 0, legacy: 0, public: 0, open: 0 };
        for (const route of routesForStatusCounts) counts[route.status] += 1;
        return counts;
    }, [routesForStatusCounts]);

    const filteredRoutes = useMemo(() => {
        if (statusFilter.length === 0) return routesForStatusCounts;
        return routesForStatusCounts.filter((route) => statusFilter.includes(route.status));
    }, [routesForStatusCounts, statusFilter]);

    const groups = useMemo(() => groupRoutesByDomain(filteredRoutes), [filteredRoutes]);

    // While any filter is active every remaining domain renders open, derived
    // rather than synced into `openDomains` so manual state returns once filters clear.
    const filtering =
        statusFilter.length > 0 || domainFilter.length > 0 || debouncedQuery.length > 0;
    const isDomainOpen = (domain: string) => filtering || openDomains.has(domain);

    const overallProgressPct = useMemo(
        () => (catalog ? migratedProgressPct(catalog.summary.byStatus) : null),
        [catalog]
    );

    useEffect(() => {
        setTitle('Routes');
        setSubtitle(
            catalog
                ? filtering
                    ? `${filteredRoutes.length} of ${catalog.summary.routeCount} routes · ${overallProgressPct ?? 0}% migrated`
                    : `${catalog.summary.routeCount} routes · ${overallProgressPct ?? 0}% migrated`
                : undefined
        );
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [catalog, overallProgressPct, filtering, filteredRoutes.length, setTitle, setSubtitle]);

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            {isError ? (
                <AccessStateNotice
                    title="Couldn't load the route catalog"
                    message="GET /api/v2/iam/catalog failed to load."
                    onRetry={() => void refresh()}
                />
            ) : isLoading || !catalog ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                </div>
            ) : (
                <>
                    {/* Status cards ARE the filter; they carry the global counts too. */}
                    <div className="shrink-0">
                        <RouteStatusCards
                            counts={statusCounts}
                            selected={statusFilter}
                            onChange={setStatusFilter}
                        />
                    </div>

                    {/* Toolbar - a search box and one dropdown, nothing else. */}
                    <div className="flex items-center justify-between gap-4 px-8 py-4 shrink-0">
                        <div className="flex items-center w-[340px] h-11 bg-white border border-gray-200 rounded-xl px-4 shrink-0">
                            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 mr-2 shrink-0" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search path, permission, or legacy guard"
                                className="bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400 w-full"
                            />
                            {query && (
                                <button
                                    type="button"
                                    onClick={() => setQuery('')}
                                    className="ml-2 shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                                    aria-label="Clear search"
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <FilterDropdown
                            title="Domain"
                            options={domainOptions}
                            selectedOptions={domainFilter}
                            onChange={setDomainFilter}
                            workQueueVariant
                            align="right"
                        />
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-8 pb-8 flex flex-col gap-3.5">
                        {groups.map((group) => (
                            <RouteDomainSection
                                key={group.domain}
                                group={group}
                                permissionLookup={permissionLookup}
                                open={isDomainOpen(group.domain)}
                                onToggle={() => toggleDomain(group.domain)}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
